import toCurrency from '../../utils/toCurrency';

import type { EntityRefType } from '@orn/global/assistant/inlineMarkup.js';

/**
 * The fields a label reads off a resolved row. Structural rather than the
 * chip's generated fragment union, so this module stays free of `__generated__`
 * imports and testable without a GraphQL client.
 */
export type EntityChipLabelTarget =
  | { __typename: 'Merchant'; name: string }
  | { __typename: 'FinancialAccount'; name: string; mask: string }
  | { __typename: 'Transaction'; amount: string }
  // `merchant` is optional-or-null the way the generated fragment types shape
  // it, so a resolved row passes through without an adapter.
  | { __typename: 'Bill'; providerName: string; merchant?: { name: string } | null }
  | { __typename: 'RecurringTransaction'; description: string; merchant?: { name: string } | null };

/**
 * What a chip says when the id resolves to nothing — hallucinated by the model,
 * or scoped away from this viewer.
 *
 * A fallback is mandatory here in a way it would not be for a linked name: the
 * model authors no label, so there is no inline text to degrade to. Rendering
 * nothing would leave a hole in the sentence ("a charge at  today"), and
 * rendering the token would put raw markup in front of the user.
 *
 * Each entry has to slot into the grammar the model wrote for the *resolved*
 * label, which is why the transaction fallback carries no determiner — a reply
 * shaped for "a larger-than-usual 75.15" reads as "a larger-than-usual this
 * transaction" the moment the lookup misses.
 */
const UNRESOLVED_LABEL: Record<EntityRefType, string> = {
  merchant: 'this merchant',
  financial_account: 'this account',
  transaction: 'transaction',
  bill: 'this bill',
  recurring_transaction: 'this recurring payment',
};

/**
 * The text a chip renders for a resolved row, or the per-type fallback when the
 * id resolved to nothing.
 *
 * Derived here, never authored by the model: the assistant emits only
 * `<merchant_id=… />` and the client resolves it, so a chip cannot name one
 * merchant while the underlying row is another.
 *
 * These renderings are quoted verbatim in `@orn/core`'s `lib/assistantMessage/enrichedMessage.ts`
 * so the model knows what it must not restate. Nothing types that link — changing
 * a label here means changing the prompt too.
 *
 * @param refType - the record type the token named; picks the fallback.
 * @param target - the resolved row, or `null` when the id did not resolve.
 */
export function entityChipLabel(refType: EntityRefType, target: EntityChipLabelTarget | null): string {
  if (target === null) {
    return UNRESOLVED_LABEL[refType];
  }
  switch (target.__typename) {
    case 'Merchant':
      return target.name;
    case 'FinancialAccount':
      // Matches how every other alert surface names an account — see
      // `utils/alertRow.ts`, which builds the same `name ••mask` suffix.
      return target.mask ? `${target.name} ••${target.mask}` : target.name;
    case 'Transaction': {
      const amount = Number(target.amount);
      // Bare: the chip draws a currency glyph, so `toCurrency`'s own `$` beside
      // it would read "$ $75.15". Still signed the way every other transaction
      // surface signs it — see `tables/TransactionsTable` and
      // `slideouts/TransactionSlideout`, which a chip click sits one step away from.
      return toCurrency(Math.abs(amount), { showSign: amount < 0, symbol: false });
    }
    // Both coalesce merchant-first because the slideouts a click opens do —
    // `BillSlideout` titles `merchant?.name ?? providerName`,
    // `RecurringTransactionSlideout` titles `merchant?.name ?? description` —
    // and a chip must agree with the surface it opens.
    case 'Bill':
      return target.merchant?.name ?? target.providerName;
    case 'RecurringTransaction':
      return target.merchant?.name ?? target.description;
    default: {
      // A target type without a label of its own falls back to saying what it
      // is rather than rendering an empty pill.
      const _exhaustive: never = target;
      void _exhaustive;
      return UNRESOLVED_LABEL[refType];
    }
  }
}
